import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { t } from '@avanta/shared/i18n';
import { getPatientName } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { PageHeader } from 'components/PageHeader';
import { EMPTY_VALUE } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { type Employee } from 'types/employees';
import { AddPatientDialog } from './AddPatientDialog';
import { type PatientListFilterKey, useFetchInsuredUsers } from './useFetchInsuredUsers';
import { usePatientsSummary } from './usePatientsSummary';
import {
  getEmployeeSessionStatusBadge,
  getSessionFilterOptions,
  getSessionStatusColumnFilter,
} from './utils';

const getColumns = (handleOnViewProfile: (patient: Employee) => void): ColumnDef<Employee>[] => [
  {
    id: 'name',
    header: t('InsuredPersonsList.Table.name.label', { ns: LOCIZE_NS.INSURANCE }),
    accessorFn: row => getPatientName(row),
    enableSorting: true,
    meta: { truncation: { maxWidthClassName: 'max-w-32' } },
    cell: ({ getValue }): React.ReactElement => (
      <span className="max-am-lg:block max-am-lg:max-w-28 max-am-lg:truncate">
        {String(getValue() ?? '')}
      </span>
    ),
  },
  {
    id: 'nif',
    accessorKey: 'nif',
    header: t('InsuredPersonsList.Table.nif.label', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    id: 'fileNumber',
    accessorFn: row => row.activeRecordCode ?? EMPTY_VALUE,
    header: t('InsuredPersonsList.Table.fileNumber.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: true,
    cell: ({ getValue }): string => String(getValue() ?? EMPTY_VALUE),
  },
  {
    id: 'center',
    accessorFn: row => row.centerName ?? EMPTY_VALUE,
    header: t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    enableSorting: true,
    meta: { truncation: { maxWidthClassName: 'max-w-32' } },
    cell: ({ getValue }): React.ReactElement => {
      const centerValue = String(getValue() ?? EMPTY_VALUE);
      return (
        <span className="max-am-lg:block max-am-lg:max-w-32 max-am-lg:truncate" title={centerValue}>
          {centerValue}
        </span>
      );
    },
  },
  {
    id: 'sessionStatus',
    accessorFn: row => row.sessionStatus,
    header: t('InsuredPersonsList.Table.sessionStatus.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: false,
    enableColumnFilter: true,
    meta: {
      columnFilter: getSessionStatusColumnFilter(),
    },
    cell: ({ getValue }): React.ReactElement =>
      getEmployeeSessionStatusBadge(getValue() as Employee['sessionStatus']),
  },
  {
    id: 'actions',
    header: t('InsuredPersonsList.Table.actions.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <Button variant="terciary" icon="leading" onClick={() => handleOnViewProfile(row.original)}>
        <UserIcon className="size-5" />
        {t('InsuredPersonsList.viewPerson.cta', { ns: LOCIZE_NS.INSURANCE })}
      </Button>
    ),
  },
];

export const Patients = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const { showDialog } = useDialogContext();

  const handleOnViewProfile = (patient: Employee): void => {
    navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patient.id }));
  };

  const columns = getColumns(handleOnViewProfile);

  const { table, selectedFilter, setFilter } = useFetchInsuredUsers(columns);
  const { stats: descriptionStats, isLoading: isDescriptionLoading } = usePatientsSummary();

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title={t('InsuredPersonsList.heading')}
        description={t('InsuredPersonsList.description', descriptionStats)}
        isDescriptionLoading={isDescriptionLoading}
      >
        <Button
          type="button"
          variant="secondary"
          size="md"
          icon="leading"
          onClick={() => showDialog({ component: <AddPatientDialog /> })}
        >
          <Plus className="size-6" strokeWidth={1.25} />
          {t('InsuredPersonsList.addPerson.cta')}
        </Button>
      </PageHeader>
      <PaginatedTable<Employee, PatientListFilterKey>
        autoPageSize
        table={table}
        columns={columns}
        emptyTitle={t('InsuredPersonsList.empty')}
        tableClassName="w-full"
        withSearch
        onRowAction={(item: Employee): void => handleOnViewProfile(item)}
        searchPlaceholder="InsuredPersonsList.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        filter={{
          accessorKey: 'hasUpcomingSession',
          allLabel: t('InsuredPersonsList.filters.all'),
          options: getSessionFilterOptions(),
        }}
        selectedFilterKey={selectedFilter}
        setFilter={setFilter}
      />
    </div>
  );
};
